import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface PortalDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  trigger: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  align?: 'left' | 'right';
  offset?: { x?: number; y?: number };
}

export const PortalDropdown: React.FC<PortalDropdownProps> = ({
  isOpen,
  onClose,
  trigger,
  children,
  className = '',
  align = 'left',
  offset = { x: 0, y: 8 }
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isMounted, setIsMounted] = useState(false);

  // Calculate dropdown position based on trigger element
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      // Reset mounted state when opening
      setIsMounted(false);
      
      const calculatePosition = () => {
        const triggerRect = triggerRef.current!.getBoundingClientRect();
        const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
        const scrollY = window.pageYOffset || document.documentElement.scrollTop;
        
        let left = triggerRect.left + scrollX + (offset.x || 0);
        let top = triggerRect.bottom + scrollY + (offset.y || 0);

        // Adjust for right alignment
        if (align === 'right' && dropdownRef.current) {
          const dropdownWidth = dropdownRef.current.offsetWidth || 250; // fallback width
          left = triggerRect.right + scrollX - dropdownWidth + (offset.x || 0);
        }

        // Ensure dropdown stays within viewport
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const dropdownWidth = dropdownRef.current?.offsetWidth || 250;
        const dropdownHeight = dropdownRef.current?.offsetHeight || 200;

        // Adjust horizontal position if it goes off-screen
        if (left + dropdownWidth > viewportWidth) {
          left = viewportWidth - dropdownWidth - 16; // 16px margin
        }
        if (left < 16) {
          left = 16; // 16px margin
        }

        // Adjust vertical position if it goes off-screen
        if (top + dropdownHeight > scrollY + viewportHeight) {
          top = triggerRect.top + scrollY - dropdownHeight - (offset.y || 0);
        }

        setPosition({ top, left });
        setIsMounted(true);
      };

      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(calculatePosition);
    } else {
      setIsMounted(false);
    }
  }, [isOpen, align, offset.x, offset.y]);

  // Handle clicks outside dropdown
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  // Handle window resize and scroll
  useEffect(() => {
    if (!isOpen) return;

    const handleWindowEvent = () => {
      if (triggerRef.current) {
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
        const scrollY = window.pageYOffset || document.documentElement.scrollTop;
        
        let left = triggerRect.left + scrollX + (offset.x || 0);
        let top = triggerRect.bottom + scrollY + (offset.y || 0);

        if (align === 'right' && dropdownRef.current) {
          const dropdownWidth = dropdownRef.current.offsetWidth || 250;
          left = triggerRect.right + scrollX - dropdownWidth + (offset.x || 0);
        }

        setPosition({ top, left });
      }
    };

    window.addEventListener('resize', handleWindowEvent);
    window.addEventListener('scroll', handleWindowEvent, true);
    
    return () => {
      window.removeEventListener('resize', handleWindowEvent);
      window.removeEventListener('scroll', handleWindowEvent, true);
    };
  }, [isOpen, align, offset.x, offset.y]);

  // Render a hidden dropdown first to measure dimensions
  const hiddenDropdown = isOpen && !isMounted ? (
    <div
      ref={dropdownRef}
      className={`fixed backdrop-blur-xl bg-white/90 rounded-2xl shadow-xl border border-white/30 overflow-hidden ${className}`}
      style={{ 
        top: -9999,
        left: -9999,
        zIndex: 9999,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        minWidth: '200px',
        maxWidth: '400px',
        visibility: 'hidden',
      }}
    >
      {children}
    </div>
  ) : null;

  const dropdown = isOpen && isMounted ? (
    <div
      className={`fixed backdrop-blur-xl bg-white/90 rounded-2xl shadow-xl border border-white/30 overflow-hidden transition-all duration-200 ${className}`}
      style={{ 
        top: position.top,
        left: position.left,
        zIndex: 9999,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        minWidth: '200px',
        maxWidth: '400px',
        opacity: 1,
        transform: 'scale(1)',
      }}
    >
      {children}
    </div>
  ) : null;

  return (
    <>
      <div ref={triggerRef}>
        {trigger}
      </div>
      {hiddenDropdown && createPortal(hiddenDropdown, document.body)}
      {dropdown && createPortal(dropdown, document.body)}
    </>
  );
};

export default PortalDropdown; 